import {
  Card,
  Space,
  Button,
  Segmented,
  Dropdown,
  Modal,
  Input,
  Tooltip,
} from "antd";
import {
  InfoCircleOutlined,
  DownloadOutlined,
  FileImageOutlined,
  FileJpgOutlined,
  FilePdfOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useMemo, useState, useRef } from "react";
import ReactECharts from "echarts-for-react";
import {
  exportChartAsPDF,
  exportChartAsImage,
  exportRadarDataAsCSV,
} from "../../../utils/export.utils";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import functions from "../../../utils/functions";
import { LOGOS } from "../../../utils/constant";
import { useResponsiveWindow } from "../../../hooks";

const { getColorPalette } = functions;

const SCALING_TOOLTIP = {
  Absolute:
    "In this mode, values are shown as-is and scaled using the global max per indicator (all designations).",
  Relative:
    "In this mode, values are scaled using the max among currently selected designations for each indicator.",
  Distribution:
    "In this mode, values are scaled as a share of the total sum of all visible designations per indicator.",
};

const RadarEngagementChart = ({ radarData = [], filters }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [footnote, setFootnote] = useState("");
  const [scalingMode, setScalingMode] = useState("Relative");
  const [enabledDesignations, setEnabledDesignations] = useState([]);
  const chartRef = useRef();
  const { username } = useAuthUser();
  const filenamePrefix = `engagement-${username}-${scalingMode.toLowerCase()}`;

  const {
    width,
    height,
    breakpoints,
    isMobile,
    isTablet,
    isSmallScreen,
    isLargeScreen,
  } = useResponsiveWindow();

  const indicators = useMemo(() => {
    return Array.from(
      new Set(radarData.flatMap((d) => d.indicators.map((i) => i.name)))
    );
  }, [radarData]);

  const legendSelected = useMemo(() => {
    const map = {};
    radarData.forEach((d) => {
      map[d.designation] =
        enabledDesignations.length === 0 ||
        enabledDesignations.includes(d.designation);
    });
    return map;
  }, [enabledDesignations, radarData]);

  const filteredData = useMemo(() => {
    return enabledDesignations.length > 0
      ? radarData.filter((d) => enabledDesignations.includes(d.designation))
      : radarData;
  }, [enabledDesignations, radarData]);

  const indicatorStats = useMemo(() => {
    const isRelativeOrDistribution = scalingMode !== "Absolute";
    const baseData = isRelativeOrDistribution
      ? enabledDesignations.length > 0
        ? radarData.filter((d) => enabledDesignations.includes(d.designation))
        : radarData
      : radarData;

    const stats = {};
    indicators.forEach((name) => {
      const values = baseData.map(
        (d) => d.indicators.find((i) => i.name === name)?.raw || 0
      );
      stats[name] = {
        max: Math.max(...values, 1),
        sum: values.reduce((acc, v) => acc + v, 0) || 1,
      };
    });
    return stats;
  }, [indicators, radarData, enabledDesignations, scalingMode]);

  const computeScaledValue = (raw, stat, mode) => {
    switch (mode) {
      case "Absolute":
        return raw;
      case "Relative":
        return raw / stat.max;
      case "Distribution":
        return raw / stat.sum;
      default:
        return raw;
    }
  };

  const tooltipFormatter = ({ name }) => {
    const d = radarData.find((r) => r.designation === name);
    if (!d) return "";

    return (
      `<b>${name}</b><ul>` +
      indicators
        .map((indicatorName) => {
          const ind = d.indicators.find((i) => i.name === indicatorName);
          const raw = ind?.raw || 0;
          const stat = indicatorStats[indicatorName];
          const scaled = computeScaledValue(raw, stat, scalingMode);
          const percent = (scaled * 100).toFixed(0);
          return `<li><b>${indicatorName}</b>: ${raw} (${percent}%)</li>`;
        })
        .join("") +
      "</ul>"
    );
  };

   // Dynamic radius, fontSize, axis name gap based on indicator count and screen
    const indicatorCount = indicators.length;
    const legendCount = filteredData.length;

    const radius = indicatorCount > 8 ? "60%" : isSmallScreen ? "80%" : "70%";
    const fontSize = indicatorCount > 8 ? 10 : isSmallScreen ? 9 : 12;
    const axisNameGap = indicatorCount > 8 ? 8 : 12;
    const chartHeight = isMobile ? 420 : 500;

  const radarOption = useMemo(() => {
    return {
      title: [
        {
          text: "Comparison by Designation",
          left: "center",
          top: 0,
          textStyle: {
            fontSize: 18,
            fontWeight: "bold",
            color: "#333",
          },
        },
        {
          text: `${filters.dateRange[0].format(
            "D MMM YYYY"
          )} - ${filters.dateRange[1].format("D MMM YYYY")}`,
          left: "center",
          top: 20,
          textStyle: {
            fontSize: 14,
            color: "#666",
          },
        },
      ],
      tooltip: { trigger: "item", formatter: tooltipFormatter },
      legend: {
        type: "scroll",
        top: "bottom",
        selected: legendSelected,
        icon: "circle",
        data: radarData.map((d) => d.designation),
      },
      radar: {
        shape: "circle",
        splitNumber: 5,
        radius,
        indicator: indicators.map((name) => ({
          name,
          max: scalingMode === "Absolute" ? indicatorStats[name].max : 1,
        })),
        axisName: {
          fontSize,
          gap: axisNameGap,
        },
      },
      series: [
        {
          type: "radar",
          data: radarData.map((d) => ({
            name: d.designation,
            value: indicators.map((indicator) => {
              const raw =
                d.indicators.find((x) => x.name === indicator)?.raw || 0;
              return computeScaledValue(
                raw,
                indicatorStats[indicator],
                scalingMode
              );
            }),
            itemStyle: { color: getColorPalette(d.designation) },
            lineStyle: { color: getColorPalette(d.designation), width: 2 },
            areaStyle: { opacity: 0.08, color: getColorPalette(d.designation) },
          })),
        },
      ],
    };
  }, [radarData, indicators, scalingMode, indicatorStats, legendSelected]);

  const onLegendChange = (e) => {
    const newActive = Object.entries(e.selected)
      .filter(([, selected]) => selected)
      .map(([k]) => k);
    setEnabledDesignations(newActive);
  };

  const handleExportPDF = async () => {
    const chartNode = chartRef.current?.getEchartsInstance();
    await exportChartAsPDF({
      chartElement: chartNode,
      filename: `${filenamePrefix}.pdf`,
      footnote,
      logos: LOGOS,
    });
    setIsModalVisible(false);
    setFootnote("");
  };

  const exportOptions = [
    {
      key: "png",
      label: "Export as PNG",
      icon: <FileImageOutlined />,
      onClick: () =>
        exportChartAsImage(chartRef, "png", `${filenamePrefix}.png`),
    },
    {
      key: "jpeg",
      label: "Export as JPEG",
      icon: <FileJpgOutlined />,
      onClick: () =>
        exportChartAsImage(chartRef, "jpeg", `${filenamePrefix}.jpeg`),
    },
    {
      key: "pdf",
      label: "Export as PDF with Footnote",
      icon: <FilePdfOutlined />,
      onClick: () => setIsModalVisible(true),
    },
    {
      key: "csv",
      label: "Export Raw Data as CSV",
      icon: <FileTextOutlined />,
      onClick: () => exportRadarDataAsCSV(radarData, scalingMode, indicators),
    },
  ];

  return (
    <Card
      extra={
        <Space>
          <Tooltip title={SCALING_TOOLTIP[scalingMode]} placement="bottom">
            <Segmented
              size="small"
              value={scalingMode}
              onChange={setScalingMode}
              options={["Absolute", "Relative", "Distribution"]}
            />
            <InfoCircleOutlined style={{ marginLeft: 6 }} />
          </Tooltip>
          <Dropdown menu={{ items: exportOptions }} trigger={["click"]}>
            <Button icon={<DownloadOutlined />} />
          </Dropdown>
        </Space>
      }
    >
      <ReactECharts
        ref={chartRef}
        option={radarOption}
        style={{ height: 500 }}
        onEvents={{ legendselectchanged: onLegendChange }}
        notMerge
        lazyUpdate
      />
      <Modal
        open={isModalVisible}
        title="Enter footnote for PDF export"
        onCancel={() => setIsModalVisible(false)}
        onOk={handleExportPDF}
      >
        <Input.TextArea
          rows={4}
          value={footnote}
          onChange={(e) => setFootnote(e.target.value)}
          placeholder="Add footnote text here..."
        />
      </Modal>
    </Card>
  );
};

export default RadarEngagementChart;
